<div class="collaborators-collapsible">
    <!-- Toggle Button -->
    @if (isEditableDetailMode) {
        <app-text-button
            class="toggle"
            [class.is-active]="isExpanded"
            [attr.aria-label]="buttonText + ' (' + collaboratorsCount + ')'"
            [attr.aria-expanded]="isExpanded"
            (click)="onButtonClick($event)"
            (keydown.enter)="onButtonClick($event)"
            (keydown.space)="onButtonClick($event)">
            <span class="collaborators-label text-sm">{{ buttonText }}</span>
            <mat-icon
                [svgIcon]="isExpanded ? 'chevron-up' : 'chevron-down'"
                class="toggle__icon"
                [attr.aria-hidden]="true" />
        </app-text-button>
    } @else {
        <app-text-button
            class="toggle"
            [class.is-active]="menuOpened"
            [matMenuTriggerFor]="collaboratorsMenu"
            [attr.aria-label]="buttonText + ' (' + collaboratorsCount + ')'"
            [attr.aria-expanded]="menuOpened"
            (click)="onButtonClick($event)"
            (keydown.enter)="onButtonClick($event)"
            (keydown.space)="onButtonClick($event)"
            (menuOpened)="onMenuOpened()"
            (menuClosed)="onMenuClosed()">
            <span class="collaborators-label text-sm">{{ buttonText }}</span>
            <mat-icon
                [svgIcon]="menuOpened ? 'chevron-up' : 'chevron-down'"
                class="toggle__icon"
                [attr.aria-hidden]="true" />
        </app-text-button>
    }

    <!-- ================================================================ -->
    <!-- Detail Mode — Inline Expanded Panel                              -->
    <!-- ================================================================ -->
    @if (isEditableDetailMode && isExpanded) {
        <div
            class="manage-panel"
            [class.manage-panel--invite]="collaboratorsCount === 0">
            <!-- Header -->
            <div class="manage-panel__header">
                <span class="text-lg text-light">
                    {{ panelTitle }}
                </span>
                <div class="manage-panel__invite-info mt-5">
                    <span class="text-s text-light">Collaborators can:</span>
                    <ul class="manage-panel__invite-list text-light">
                        <li>
                            View all data associated with
                            <span class="manage-panel__document-name">{{
                                documentName
                            }}</span>
                            and its discrepancies.
                        </li>
                        <li>
                            Create new discrepancies, manage collaborators and
                            add comments.
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Search -->
            <div class="manage-panel__search">
                <div class="search-chip-input">
                    @if (pendingCollaborators.length > 0) {
                        <div class="search-chip-input__chips" #chipsContainer>
                            @for (
                                collaborator of pendingCollaborators;
                                track collaborator.id
                            ) {
                                <span class="search-chip-input__chip">
                                    <span
                                        class="search-chip-input__chip-label text-xs text-light">
                                        {{ collaborator.fullName }}
                                    </span>
                                    <button
                                        type="button"
                                        class="search-chip-input__chip-remove"
                                        [attr.aria-label]="
                                            'Remove pending ' +
                                            collaborator.fullName
                                        "
                                        (click)="
                                            removePendingCollaborator(
                                                collaborator
                                            )
                                        ">
                                        <mat-icon svgIcon="close" />
                                    </button>
                                </span>
                            }
                        </div>
                    }

                    <app-autocomplete-fixers
                        class="search-chip-input__autocomplete"
                        variant="secondary"
                        placeholder="Search by names or e-mail addresses"
                        [dataQa]="dataQa.manageCollaboratorsDialogSearchInput"
                        [inputControl]="searchControl"
                        [options]="candidateOptions"
                        [openOnFocus]="true"
                        [showNoResults]="true"
                        noResultsText="No matching results."
                        [autoActiveFirstOption]="false"
                        (selectionChange)="onCandidateSelected($event)" />
                </div>
            </div>

            <!-- Existing Collaborators -->
            @if (collaborators.length > 0) {
                <div class="manage-panel__collaborators">
                    <span class="manage-panel__section-title text-sm text-med">
                        Existing Collaborators ({{ collaboratorsCount }})
                    </span>
                    <ul class="manage-panel__list">
                        @for (
                            collaborator of collaborators;
                            track collaborator.id
                        ) {
                            <li
                                class="manage-panel__list-item"
                                [class.manage-panel__list-item--inactive]="
                                    isInactiveCollaborator(collaborator)
                                ">
                                <app-tooltip>
                                    <span
                                        trigger
                                        class="manage-panel__list-item-text text-sm">
                                        {{
                                            getCollaboratorDisplayText(
                                                collaborator
                                            )
                                        }}
                                    </span>
                                    <div content class="text-xs">
                                        {{
                                            getCollaboratorDisplayText(
                                                collaborator
                                            )
                                        }}
                                    </div>
                                </app-tooltip>
                                @if (isInactiveCollaborator(collaborator)) {
                                    <span
                                        class="inactive-badge text-xxs text-light color-gray11">
                                        <mat-icon svgIcon="info" />
                                        Inactive
                                    </span>
                                }
                                <button
                                    class="manage-panel__remove-btn"
                                    type="button"
                                    [attr.data-qa]="
                                        dataQa.manageCollaboratorsDialogRemoveCollaborator
                                    "
                                    [attr.aria-label]="
                                        'Remove ' + collaborator.fullName
                                    "
                                    (click)="removeCollaborator(collaborator)">
                                    <mat-icon svgIcon="close" />
                                </button>
                            </li>
                        }
                    </ul>
                </div>
            }

            <!-- Footer -->
            <div class="manage-panel__footer">
                <app-flat-button
                    [dataQa]="dataQa.manageCollaboratorsDialogCancel"
                    (clicked)="cancelPanel()">
                    Cancel
                </app-flat-button>
                <app-flat-button
                    variant="cta"
                    [disabled]="isSaveDisabled"
                    [dataQa]="dataQa.manageCollaboratorsDialogSave"
                    (clicked)="save()">
                    Save
                </app-flat-button>
            </div>
        </div>
    }

    <!-- ================================================================ -->
    <!-- Summary Mode — Read-only Dropdown (mat-menu)                     -->
    <!-- ================================================================ -->
    <mat-menu
        #collaboratorsMenu="matMenu"
        class="collaborators-menu"
        [hasBackdrop]="false"
        yPosition="below"
        xPosition="after">
        <section class="collaborators-content">
            @if (isLoading) {
                <output class="loading-state" aria-live="polite">
                    <app-loader />
                </output>
            } @else if (collaborators.length === 0) {
                <div class="collaborators-header">
                    <span class="collaborators-title text-sm text-med">
                        {{ buttonText }} ({{ collaboratorsCount }})
                    </span>
                </div>
                <output class="empty-state text-sm text-light color-gray10">
                    <span>No collaborators to show.</span>
                </output>
            } @else {
                <!-- Header -->
                <div class="collaborators-header">
                    <span class="collaborators-title text-sm text-med">
                        {{ buttonText }} ({{ collaboratorsCount }})
                    </span>
                </div>

                <!-- List -->
                <ul
                    class="collaborators-list"
                    [attr.aria-label]="buttonText + ' list'">
                    @for (
                        collaborator of collaborators;
                        track collaborator.id
                    ) {
                        <li
                            class="collaborator-item"
                            [class.collaborator-item--inactive]="
                                isInactiveCollaborator(collaborator)
                            ">
                            @if (needsTooltip(collaborator)) {
                                <app-tooltip>
                                    <span
                                        trigger
                                        class="collaborator-text text-sm text-light">
                                        {{
                                            getCollaboratorDisplayText(
                                                collaborator
                                            )
                                        }}
                                    </span>
                                    <div content class="text-xs">
                                        {{
                                            getCollaboratorDisplayText(
                                                collaborator
                                            )
                                        }}
                                    </div>
                                </app-tooltip>
                            } @else {
                                <span
                                    class="collaborator-text text-sm text-light">
                                    {{
                                        getCollaboratorDisplayText(collaborator)
                                    }}
                                </span>
                            }
                            @if (isInactiveCollaborator(collaborator)) {
                                <span
                                    class="inactive-badge text-xxs text-light color-gray11">
                                    <mat-icon svgIcon="info" />
                                    Inactive
                                </span>
                            }
                        </li>
                    }
                </ul>
            }
        </section>
    </mat-menu>
</div>
